/**
 * hexLayout — pure geometry for the Alert Board's honeycomb canvas (shared local + cloud).
 *
 *   packCluster()  arranges one group's cameras into a flat-top honeycomb BLOCK.
 *   layoutSites()  lays each site's packed groups out GROUPS_PER_ROW to a row across the
 *                  canvas INSIDE that site's own panel, stacks the panels vertically, and
 *                  gives every hex an ABSOLUTE canvas-space center {cx,cy} — the anchor for
 *                  the floating preview / hover popover.
 *
 * The row is a fixed grid of equal columns sized to the width the page actually has, and a
 * group's cameras fill a squarest-possible block inside its column (see colsForCount) so the
 * group reads as one scannable unit rather than as a long strip the eye has to track across.
 *
 * Two levels of framing, not one: the board reads site → group → camera, and when the
 * groups of two sites flowed into the same wrapping row an operator could not tell where
 * one site ended and the next began. Each site now owns a panel; groups only ever wrap
 * within it.
 *
 * Geometry constants live here (the canonical source) so shared hex components
 * don't depend on any app's local files.
 */
// Flat-top hexagons: wider than tall (the pointy-top orientation rotated 90°).
export const HEX_W = 144;
export const HEX_H = 128;
export const HEX_COL_OVERLAP = 36; // horizontal interlock between columns
export const HEX_CLIP = 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)';

export const HEX_GAP = 12; // vertical gap between hexes in a column
export const STEP_X = HEX_W - HEX_COL_OVERLAP; // column pitch (¾ width → flat-top interlock)
export const STEP_Y = HEX_H + HEX_GAP; // row pitch

/** Group header block: name + state badge, then the location/count line, then its rule. */
export const TITLE_H = 46;
/**
 * Fewest hexes a group's row may hold. It sets the floor on the width a group column is
 * given, so a narrow window never squeezes a group's block below the 4-wide shape that
 * counts of 10–16 ask for.
 *
 * Expressed in hexes, not px: what a column has to be wide enough for is HEXES, and a width
 * that fits 3.9 of them wastes the remainder while pushing a camera onto its own row.
 */
export const MIN_ROW_HEXES = 4;
export const CLUSTER_GAP_X = 44;
export const CLUSTER_GAP_Y = 40;

/** Site panel: header band height and the padding inside the panel's border. */
export const SITE_HEADER_H = 30;
export const SITE_PAD = 18;
export const SITE_GAP_Y = 34;

/** Fallback flow width, used only before the page has measured its canvas. */
export const TARGET_W = 1280;
/** Breathing room around the whole board. Deliberately small: the canvas is the content,
 *  and every px here is width the honeycomb doesn't get. */
export const CANVAS_PAD = 12;

/** Width of a strip of `cols` interlocked flat-top hexes. */
export const stripWidth = (cols: number) => (cols <= 0 ? 0 : (cols - 1) * STEP_X + HEX_W);

/** How many hexes fit in one strip of `width` px (at least one). */
export const colsForWidth = (width: number) => Math.max(1, Math.floor((width - HEX_W) / STEP_X) + 1);

export interface ClusterInput<T> {
  id: string;
  title: string;
  subtitle?: string;
  items: T[];
}

/**
 * One site and the groups inside it, in the order they should be laid out.
 *
 * An EMPTY name means "there is no site level here" — a single-site node, where every group
 * belongs to the same place and saying so once per board would be saying nothing. The panel
 * still exists (it is the common background the groups sit on) but carries no header, and
 * no space is reserved for one.
 */
export interface SiteInput<T> {
  id: string;
  name: string;
  clusters: ClusterInput<T>[];
}

export interface PositionedHex<T> {
  item: T;
  x: number;
  y: number;
  cx: number;
  cy: number;
}

export interface LaidOutCluster<T> {
  id: string;
  title: string;
  subtitle?: string;
  originX: number;
  originY: number;
  width: number;
  height: number;
  hexes: PositionedHex<T>[];
}

export interface LaidOutSite<T> {
  id: string;
  name: string;
  /** Cameras across every group in this site — the panel header's count. */
  cameraCount: number;
  /** The panel box, in canvas space (border included). */
  originX: number;
  originY: number;
  width: number;
  height: number;
  clusters: LaidOutCluster<T>[];
}

export interface CanvasLayout<T> {
  sites: LaidOutSite<T>[];
  /** Every hex on the board, across every site — for callers that need a flat list. */
  hexes: PositionedHex<T>[];
  width: number;
  height: number;
}

interface PackedHex<T> {
  item: T;
  x: number;
  y: number;
}
interface Packed<T> {
  hexes: PackedHex<T>[];
  width: number;
  height: number;
}

/**
 * Row/column count for a group of `n` cameras — the shape the group takes before the
 * available width has any say. ceil(√n), i.e. the squarest block the count allows:
 *
 *   1–2 → 1 row      3–4 → 2 rows × 2 cols     5–6 → 2 rows × 3 cols
 *   7–9 → 3 rows × 3 cols   10–12 → 3 rows × 4 cols   13–16 → 4 rows × 4 cols
 *
 * A group is a UNIT the operator scans as a whole, and a block is scanned in one glance
 * where a long strip has to be read end to end. It also keeps every group's footprint
 * close to square, so a row of groups with different counts still reads as one line of
 * tiles rather than as bars of wildly different lengths.
 */
export const colsForCount = (n: number) => (n <= 0 ? 1 : Math.ceil(Math.sqrt(n)));

/**
 * Pack one group into interlocked hex rows: the block shape from colsForCount(), narrowed
 * to `maxCols` when that block is wider than the group's column of the row can hold (an
 * overflowing group simply takes more rows rather than overrunning its neighbour).
 */
function packCluster<T>(items: T[], maxCols: number): Packed<T> {
  const n = items.length;
  if (n === 0) return { hexes: [], width: STEP_X, height: HEX_H };
  const cols = Math.max(1, Math.min(maxCols, colsForCount(n)));

  const hexes: PackedHex<T>[] = [];
  let maxRight = 0;
  let maxBottom = 0;
  items.forEach((item, i) => {
    const row = Math.floor(i / cols);
    const col = i % cols;
    // Flat-top honeycomb: columns interlock horizontally (¾-width pitch) and every
    // odd column is dropped half a row so the hexes tessellate without gaps.
    const x = col * STEP_X;
    const y = row * STEP_Y + (col % 2 ? STEP_Y / 2 : 0);
    hexes.push({ item, x, y });
    maxRight = Math.max(maxRight, x + HEX_W);
    maxBottom = Math.max(maxBottom, y + HEX_H);
  });
  return { hexes, width: maxRight, height: maxBottom };
}

/**
 * Groups per row. FIXED rather than "as many as fit": a board whose columns land in the same
 * places every time can be read positionally — the operator learns where a group lives and
 * goes straight there — whereas a count that changed with the window meant the same group
 * moved every time the browser was resized.
 */
export const GROUPS_PER_ROW = 3;

export interface LayoutOptions {
  /** Override the fixed groups-per-row (defaults to GROUPS_PER_ROW). */
  groupsPerRow?: number;
  /**
   * The width the board has to fill, in px — normally the canvas element's own width, so the
   * row's columns are sized against the operator's actual screen rather than a number picked
   * in this file. Omit before the page has measured (TARGET_W stands in).
   */
  targetWidth?: number;
}

/**
 * Lay every site out as its own panel, stacked top to bottom; inside each panel the site's
 * groups sit in a fixed GROUPS_PER_ROW grid across the available width. Hex positions come
 * back absolute, so the page can anchor screen-space overlays to any hex without knowing
 * which panel it is in.
 */
export function layoutSites<T>(
  sites: SiteInput<T>[],
  { targetWidth, groupsPerRow = GROUPS_PER_ROW }: LayoutOptions = {},
): CanvasLayout<T> {
  const out: LaidOutSite<T>[] = [];
  const allHexes: PositionedHex<T>[] = [];

  // The width groups actually flow into: the canvas, less the canvas margin and the panel's
  // own padding. Floored at two hexes so a narrow window degrades instead of collapsing.
  // Panel padding is only paid for where a panel is actually drawn (a named site). A
  // single-site board frames nothing, so that inset would be dead space on both sides.
  const sitePad = sites.some((s) => s.name) ? SITE_PAD : 0;
  const flowW = Math.max(
    stripWidth(2),
    (targetWidth && targetWidth > 0 ? targetWidth : TARGET_W) - CANVAS_PAD * 2 - sitePad * 2,
  );
  const perRow = Math.max(1, groupsPerRow);
  // The share of the row one group may use up. It caps how wide a group's hex strip can get;
  // groups narrower than that keep their own width (see the placement loop).
  // A column too narrow for MIN_ROW_HEXES would file the group into a near-vertical stack.
  // Rather than allow that, the row is permitted to overrun the canvas — "Fit" then scales
  // the whole board down, which keeps every group's shape and costs only size.
  const colW = Math.max(stripWidth(MIN_ROW_HEXES), (flowW - CLUSTER_GAP_X * (perRow - 1)) / perRow);
  const maxCols = colsForWidth(colW);

  let cursorY = CANVAS_PAD;
  let canvasRight = 0;

  for (const site of sites) {
    const packed = site.clusters.map((c) => ({ input: c, pack: packCluster(c.items, maxCols) }));

    // Groups flow inside the panel's content box, whose origin is (0,0) here and is
    // translated onto the canvas below.
    const headerH = site.name ? SITE_HEADER_H : 0;
    const clustersLeft = CANVAS_PAD + sitePad;
    const clustersTop = cursorY + sitePad + headerH;

    let x = 0;
    let y = 0;
    let rowHeight = 0;
    let contentRight = 0;
    const clusters: LaidOutCluster<T>[] = [];

    packed.forEach(({ input, pack }, i) => {
      const col = i % perRow;
      // A new row starts every `perRow` groups, and its top clears the TALLEST group of the
      // row above (groups differ in height whenever their camera counts differ).
      if (col === 0 && i > 0) {
        x = 0;
        y += rowHeight + CLUSTER_GAP_Y;
        rowHeight = 0;
      }

      // Each group is exactly as wide as its own block. `colW` is a CEILING (the equal share
      // of the row), not an allocation: a group of two would otherwise sit in a four-wide
      // column with half of it blank, and a row of small groups would read as mostly gaps.
      const clusterW = pack.width;
      const clusterH = TITLE_H + pack.height;

      const originX = clustersLeft + x;
      const originY = clustersTop + y;
      const hexTop = originY + TITLE_H;

      const hexes: PositionedHex<T>[] = pack.hexes.map((h) => ({
        item: h.item,
        x: originX + h.x,
        y: hexTop + h.y,
        cx: originX + h.x + HEX_W / 2,
        cy: hexTop + h.y + HEX_H / 2,
      }));
      allHexes.push(...hexes);

      clusters.push({
        id: input.id,
        title: input.title,
        subtitle: input.subtitle,
        originX,
        originY,
        width: clusterW,
        height: clusterH,
        hexes,
      });

      x += clusterW + CLUSTER_GAP_X;
      contentRight = Math.max(contentRight, x - CLUSTER_GAP_X);
      rowHeight = Math.max(rowHeight, clusterH);
    });

    const contentH = y + rowHeight;
    const width = contentRight + sitePad * 2;
    const height = sitePad * 2 + headerH + contentH;

    out.push({
      id: site.id,
      name: site.name,
      cameraCount: site.clusters.reduce((n, c) => n + c.items.length, 0),
      originX: CANVAS_PAD,
      originY: cursorY,
      width,
      height,
      clusters,
    });

    canvasRight = Math.max(canvasRight, CANVAS_PAD + width);
    cursorY += height + SITE_GAP_Y;
  }

  // Panels are stacked cards; ragged right edges would read as an accident rather than as a
  // set, so every panel takes the width of the widest. Their CONTENT stays left-aligned, so
  // nothing moves — only the frame grows.
  const panelW = out.reduce((w, s) => Math.max(w, s.width), 0);
  out.forEach((s) => {
    s.width = panelW;
  });

  return {
    sites: out,
    hexes: allHexes,
    width: canvasRight + CANVAS_PAD,
    // The trailing SITE_GAP_Y is not content — drop it so "Fit" doesn't leave dead space.
    height: (out.length ? cursorY - SITE_GAP_Y : cursorY) + CANVAS_PAD,
  };
}
